import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Tabs,
    Tab,
    CircularProgress,
    Paper,
    Chip,
    Alert,
    LinearProgress,
    alpha,
    useTheme,
    Avatar
} from '@mui/material';
import {
    EmojiEvents as TrophyIcon,
    Psychology as PsychologyIcon,
    Stars as StarsIcon,
    CalendarMonth as CalendarIcon,
    Lock as LockIcon,
    WorkspacePremium as PremiumIcon
} from '@mui/icons-material';
import StudentLayout from 'components/layout/StudentLayout';
import { getAllAchievements, getUserAchievements, getAchievementStats } from 'services/achievementService';
import { Achievement, StudentAchievement, AchievementStats } from 'types/achievement';
import AchievementCard from 'components/achievements/AchievementCard';
import { getIconComponent } from 'components/achievements/AchievementUtils';

// Achievement Stats Component
function AchievementStatsCard({ stats }: { stats: AchievementStats }) {
    const theme = useTheme();

    return (
        <Box sx={{ mb: 4 }}>
            {/* Header Stats */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                    <Box
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                            textAlign: 'center',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 8px 24px ${alpha(theme.palette.warning.main, 0.15)}`
                            }
                        }}
                    >
                        <Avatar
                            sx={{
                                bgcolor: theme.palette.warning.main,
                                width: 56,
                                height: 56,
                                mx: 'auto',
                                mb: 2
                            }}
                        >
                            <TrophyIcon sx={{ fontSize: 32 }} />
                        </Avatar>
                        <Typography variant="h3" fontWeight="700" sx={{ color: theme.palette.warning.dark, mb: 1 }}>
                            {stats.totalEarned}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" fontWeight="600">
                            Achievements Earned
                        </Typography>
                    </Box>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Box
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            border: `1px solid ${theme.palette.divider}`,
                            background: 'background.paper',
                            height: '100%'
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" fontWeight="600">
                                Achievement Progress
                            </Typography>
                            <Chip
                                label={`${stats.percentageEarned}%`}
                                color={stats.percentageEarned > 75 ? 'success' : stats.percentageEarned > 50 ? 'primary' : 'default'}
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    height: 32
                                }}
                            />
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={stats.percentageEarned}
                            sx={{
                                height: 12,
                                borderRadius: 6,
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                '& .MuiLinearProgress-bar': {
                                    background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.primary.main} 100%)`,
                                    borderRadius: 6
                                }
                            }}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            <PremiumIcon fontSize="small" color="primary" />
                            Keep solving problems to unlock more achievements!
                        </Typography>
                    </Box>
                </Grid>
            </Grid>

            {/* Rarest Achievement */}
            {stats.highestRarity && stats.highestRarity.achievement && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.1)}`,
                            transform: 'translateY(-2px)'
                        }
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        fontWeight="600"
                        gutterBottom
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 2
                        }}
                    >
                        <PsychologyIcon color="secondary" />
                        Your Rarest Achievement
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                            sx={{
                                bgcolor: alpha(theme.palette.secondary.main, 0.15),
                                color: theme.palette.secondary.main,
                                width: 56,
                                height: 56
                            }}
                        >
                            {getIconComponent(stats.highestRarity.achievement.iconName)}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" fontWeight="700" sx={{ color: theme.palette.secondary.dark }}>
                                {stats.highestRarity.achievement.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ my: 0.5 }}>
                                {stats.highestRarity.achievement.description}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: theme.palette.secondary.main,
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                }}
                            >
                                <CalendarIcon sx={{ fontSize: 14 }} />
                                Earned on {new Date(stats.highestRarity.awardedAt).toLocaleDateString()}
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            )}
        </Box>
    );
}

// Main Component
function AchievementsPageV2() {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [allAchievements, setAllAchievements] = useState<Achievement[]>([]);
    const [userAchievements, setUserAchievements] = useState<StudentAchievement[]>([]);
    const [stats, setStats] = useState<AchievementStats>({
        totalEarned: 0,
        percentageEarned: 0,
        recentlyEarned: [],
        highestRarity: null
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const [all, user, achievementStats] = await Promise.all([
                    getAllAchievements(),
                    getUserAchievements(),
                    getAchievementStats()
                ]);

                setAllAchievements(all);
                setUserAchievements(user);
                setStats(achievementStats);
            } catch (err) {
                console.error('Error fetching achievements:', err);
                setError('Failed to load achievements. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    // Get earned achievements IDs for quick lookup
    const earnedAchievementIds = new Set(userAchievements.map(ua => ua.achievementId));

    // Get the earned achievement date by achievement ID
    const getEarnedDate = (achievementId: number): Date | undefined => {
        const earned = userAchievements.find(ua => ua.achievementId === achievementId);
        return earned?.awardedAt;
    };

    return (
        <StudentLayout>
            <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
                {/* Header */}
                <Box
                    sx={{
                        mb: 4,
                        p: 4,
                        borderRadius: 4,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
                    }}
                >
                    <Typography
                        variant="h3"
                        fontWeight="800"
                        sx={{
                            background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.secondary.main})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5
                        }}
                    >
                        <TrophyIcon sx={{ fontSize: 40, color: theme.palette.warning.main }} />
                        Achievement Trophy Case
                    </Typography>
                    <Typography variant="h6" color="text.secondary" fontWeight="400">
                        Track your progress and showcase your math achievements
                    </Typography>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
                        {error}
                    </Alert>
                ) : allAchievements.length === 0 ? (
                    <Alert severity="warning" sx={{ mb: 3, borderRadius: 3 }}>
                        No achievements found in the system. The achievement system may not be properly set up.
                    </Alert>
                ) : (
                    <>
                        <AchievementStatsCard stats={stats} />

                        {/* Tabs */}
                        <Paper
                            elevation={0}
                            sx={{
                                mb: 4,
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.divider}`,
                                overflow: 'hidden'
                            }}
                        >
                            <Tabs
                                value={tabValue}
                                onChange={handleTabChange}
                                variant="fullWidth"
                                sx={{
                                    bgcolor: 'background.paper',
                                    '& .MuiTabs-indicator': {
                                        height: 3,
                                        borderRadius: '3px 3px 0 0'
                                    },
                                    '& .MuiTab-root': {
                                        fontWeight: 600,
                                        fontSize: '0.95rem',
                                        py: 2,
                                        textTransform: 'none',
                                        '&.Mui-selected': {
                                            fontWeight: 700
                                        }
                                    }
                                }}
                            >
                                <Tab
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <StarsIcon fontSize="small" />
                                            <span>All Achievements</span>
                                        </Box>
                                    }
                                />
                                <Tab
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TrophyIcon fontSize="small" />
                                            <span>Earned</span>
                                            <Chip
                                                label={stats.totalEarned}
                                                size="small"
                                                color="success"
                                                sx={{ ml: 0.5, fontWeight: 700 }}
                                            />
                                        </Box>
                                    }
                                />
                                <Tab
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LockIcon fontSize="small" />
                                            <span>Locked</span>
                                        </Box>
                                    }
                                />
                            </Tabs>
                        </Paper>

                        {/* Achievement Grid */}
                        <Grid container spacing={3}>
                            {allAchievements.length === 0 ? (
                                <Box sx={{ textAlign: 'center', width: '100%', py: 3 }}>
                                    <Typography variant="body1">No achievements found</Typography>
                                </Box>
                            ) : (
                                <>
                                    {tabValue === 0 && allAchievements
                                        .sort((a, b) => {
                                            // Sort earned achievements first, then by ID
                                            const aEarned = earnedAchievementIds.has(a.id);
                                            const bEarned = earnedAchievementIds.has(b.id);
                                            if (aEarned && !bEarned) return -1;
                                            if (!aEarned && bEarned) return 1;
                                            return a.id - b.id;
                                        })
                                        .map(achievement => (
                                            <Grid item xs={12} sm={6} md={4} key={achievement.id}>
                                                <AchievementCard
                                                    achievement={achievement}
                                                    earned={earnedAchievementIds.has(achievement.id)}
                                                    earnedDate={getEarnedDate(achievement.id)}
                                                />
                                            </Grid>
                                        ))}

                                    {tabValue === 1 && (
                                        userAchievements.length > 0 ? (
                                            userAchievements
                                                .filter(ua => ua.achievement)
                                                .sort((a, b) => {
                                                    return new Date(b.awardedAt).getTime() - new Date(a.awardedAt).getTime();
                                                })
                                                .map(userAchievement => userAchievement.achievement ? (
                                                    <Grid item xs={12} sm={6} md={4} key={userAchievement.id}>
                                                        <AchievementCard
                                                            achievement={userAchievement.achievement}
                                                            earned={true}
                                                            earnedDate={userAchievement.awardedAt}
                                                        />
                                                    </Grid>
                                                ) : null)
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    p: 6,
                                                    textAlign: 'center',
                                                    borderRadius: 3,
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    bgcolor: 'background.paper'
                                                }}
                                            >
                                                <TrophyIcon sx={{ fontSize: 64, color: theme.palette.action.disabled, mb: 2 }} />
                                                <Typography variant="h5" fontWeight="600" gutterBottom>
                                                    No achievements yet
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                                    You haven't earned any achievements yet. Keep working on problems to unlock achievements!
                                                </Typography>
                                                <Alert severity="info" sx={{ mt: 3, maxWidth: 400, mx: 'auto' }}>
                                                    Tip: Try solving your first math problem to earn the "Math Rookie" achievement!
                                                </Alert>
                                            </Box>
                                        )
                                    )}

                                    {tabValue === 2 && (
                                        allAchievements
                                            .filter(achievement => !earnedAchievementIds.has(achievement.id))
                                            .length > 0 ? (
                                            allAchievements
                                                .filter(achievement => !earnedAchievementIds.has(achievement.id))
                                                .map(achievement => (
                                                    <Grid item xs={12} sm={6} md={4} key={achievement.id}>
                                                        <AchievementCard
                                                            achievement={achievement}
                                                            earned={false}
                                                        />
                                                    </Grid>
                                                ))
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    p: 6,
                                                    textAlign: 'center',
                                                    borderRadius: 3,
                                                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                                    bgcolor: alpha(theme.palette.success.main, 0.05)
                                                }}
                                            >
                                                <TrophyIcon sx={{ fontSize: 64, color: theme.palette.success.main, mb: 2 }} />
                                                <Typography variant="h5" fontWeight="700" color="success.dark" gutterBottom>
                                                    Congratulations!
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary">
                                                    You've earned all available achievements! Keep up the great work!
                                                </Typography>
                                            </Box>
                                        )
                                    )}
                                </>
                            )}
                        </Grid>
                    </>
                )}
            </Box>
        </StudentLayout>
    );
}

export default AchievementsPageV2;
